import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_KONSOLIDIEREN } from "../makros";
import { formelAufgabe } from "../formelaufgabe";

/** 3D-Bezug wie Jan:Mär!B2 oder 'Jan 2025:Mär 2025'!B2. */
const DREI_D = /('[^']+:[^']+'|[\p{L}\p{N}_.]+:[\p{L}\p{N}_.]+)!\$?[A-Z]+\$?\d+/u;

export const lektionKonsolidieren: Lesson = {
  id: "konsolidieren",
  title: "Blätter zusammenfassen: 3D-Bezüge und Konsolidieren",
  summary: "Januar, Februar, März – und ein Blatt, das alles addiert.",
  durationMinutes: 11,
  learningGoals: [
    "Gleich aufgebaute Blätter mit einem 3D-Bezug summieren",
    "Unterschiedlich aufgebaute Listen mit „Konsolidieren“ zusammenführen",
  ],
  steps: [
    {
      id: "dreid-bezug",
      title: "Der 3D-Bezug",
      explanation: [
        "Haben mehrere Blätter denselben Aufbau – etwa je Monat ein Blatt, Umsatz immer in " +
          "B2 –, summiert ein 3D-Bezug über alle: =SUMME(Jan:Mär!B2). Er umfasst jedes Blatt, " +
          "das im Register zwischen „Jan“ und „Mär“ liegt.",
        "Ein Trick macht das robust: zwei leere Blätter „Start“ und „Ende“ als Klammer, " +
          "=SUMME(Start:Ende!B2). Jedes Monatsblatt, das man dazwischen zieht, wird " +
          "mitgezählt.",
      ],
      keyPoints: [
        "=SUMME(Erstes:Letztes!Zelle).",
        "Alle Blätter dazwischen zählen mit – die Reihenfolge im Register entscheidet.",
        "Leere Klammerblätter „Start“ und „Ende“.",
      ],
      task: {
        instruction:
          "Lege drei gleich aufgebaute Blätter an und summiere eine Zelle über alle drei mit einem 3D-Bezug.",
        hint: "=SUMME(Jan:Mär!B2) – Blattnamen an deine anpassen.",
        check: formelAufgabe(
          [{ label: "Eine Formel mit 3D-Bezug über mehrere Blätter", test: (formel) => DREI_D.test(formel) }],
          {
            success: "Eine Formel, drei Blätter – und jedes weitere dazwischen zählt mit.",
            failure: "Es gibt noch keine Formel mit 3D-Bezug.",
            hint: "Der Doppelpunkt steht zwischen den Blattnamen: Jan:Mär!B2.",
          },
        ),
      },
    },
    {
      id: "konsolidieren",
      title: "Daten konsolidieren",
      explanation: [
        "Sind die Listen nicht gleich aufgebaut – andere Reihenfolge, fehlende Zeilen –, " +
          "hilft ein 3D-Bezug nicht. „Daten“ → „Konsolidieren“ fasst sie über die " +
          "Beschriftungen zusammen: Funktion wählen (Summe), jeden Quellbereich einmal " +
          "„Hinzufügen“, unten „Oberste Zeile“ und „Linke Spalte“ ankreuzen.",
        "Mit „Verknüpfungen mit Quelldaten“ entsteht eine Gliederung mit Formeln, die sich " +
          "bei Änderungen nachziehen; ohne das Häkchen sind es feste Werte.",
      ],
      keyPoints: [
        "Daten → Konsolidieren → Quellbereiche hinzufügen.",
        "„Oberste Zeile“ und „Linke Spalte“ = nach Beschriftung zusammenführen.",
        "„Verknüpfungen mit Quelldaten“ für nachziehende Ergebnisse.",
      ],
      task: {
        instruction:
          "Führe mindestens zwei Listen mit „Daten“ → „Konsolidieren“ auf einem eigenen Blatt zusammen.",
        hint: "Auf dem Zielblatt A1 wählen, Daten → Konsolidieren, Bereiche hinzufügen, Beschriftung aus oberster Zeile und linker Spalte.",
        macro: MAKRO_KONSOLIDIEREN,
        check: () =>
          pruefeMitMakro(MAKRO_KONSOLIDIEREN, (bericht) => {
            const ziele = [...bericht.gruppe("ziel").values()];
            const mitQuellen = ziele.filter((ziel) => Number(ziel.get("quellen") ?? 0) >= 2);
            return fromCriteria(
              [
                criterion(
                  mitQuellen[0]
                    ? `Auf „${mitQuellen[0].get("blatt")}“ sind ${mitQuellen[0].get("quellen")} Bereiche konsolidiert`
                    : "Ein Blatt konsolidiert mindestens zwei Quellbereiche",
                  mitQuellen.length > 0,
                ),
              ],
              {
                success: "Zusammengeführt über die Beschriftungen – auch bei unterschiedlichem Aufbau.",
                failure: "Es wurde noch nichts mit mindestens zwei Quellen konsolidiert.",
              },
            );
          }),
      },
    },
  ],
};
